import { FC, useState } from 'react';
import { DataRoomStatus, MAX_SPACE_DESCRIPTION_LENGTH, MAX_SPACE_NAME_LENGTH } from 'shared';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Input } from '../../../../components/input/index';
import Loader from '../../../../components/loader/index';
import { ModalWrapper } from '../../../../components/modal-wrapper/index';
import { Switch } from '../../../../components/switch/index';
import { useLocale } from '../../../../hooks/index';
import { useDataRoom, usePatchDataRoom } from '../../../../hooks/space/index';
import { DataRoomModalType } from '../../enums/data-room-modal-type.enum';
import { DataRoomModalStore } from '../../store/data-room-modal.store';
import { keepFocusInsideDialog } from './keep-focus-inside-dialog.util';
import { CharacterLimitMessage } from './space-details-fields';

// Shadow/sm from the design, on the knob of every toggle in this modal.
const KNOB_SHADOW =
  'shadow-[0px_1px_2px_0px_rgba(16,24,40,0.06),0px_1px_3px_0px_rgba(16,24,40,0.10)]';

type SettingsForm = {
  description: string;
  isActive: boolean;
  name: string;
  showInFolderStats: boolean;
};

export const DataRoomSettingsModal: FC = () => {
  const isOpen = DataRoomModalStore(({ isOpen }) => isOpen);
  const type = DataRoomModalStore(({ type }) => type);
  const dataRoomId = DataRoomModalStore(({ metadata }) => metadata?.spaceId);
  const handleDismiss = DataRoomModalStore(({ handleDismiss }) => handleDismiss);
  const openModal = DataRoomModalStore(({ openModal }) => openModal);

  const { messagesLocale } = useLocale();
  const { dataRoom, isLoading } = useDataRoom(dataRoomId);
  const { isPending, patchDataRoom } = usePatchDataRoom();

  const [form, setForm] = useState<null | SettingsForm>(null);
  const [loadedId, setLoadedId] = useState<string | undefined>(undefined);

  // The form is seeded once from the loaded room; a later refetch must not overwrite what the
  // person is typing.
  if (dataRoom && dataRoom.id !== loadedId) {
    setLoadedId(dataRoom.id);
    setForm({
      description: dataRoom.description ?? '',
      isActive: dataRoom.status === DataRoomStatus.ACTIVE,
      name: dataRoom.name,
      showInFolderStats: dataRoom.settings.showInFolderStats,
    });
  }

  if (type !== DataRoomModalType.DATA_ROOM_SETTINGS || !dataRoomId) {
    return null;
  }

  const trimmedName = form?.name.trim() ?? '';
  const trimmedDescription = form?.description.trim() ?? '';

  const isDirty =
    !!form &&
    !!dataRoom &&
    (trimmedName !== dataRoom.name ||
      trimmedDescription !== (dataRoom.description ?? '') ||
      form.isActive !== (dataRoom.status === DataRoomStatus.ACTIVE) ||
      form.showInFolderStats !== dataRoom.settings.showInFolderStats);

  const save = () => {
    if (!form || !trimmedName || !isDirty || isPending) return;

    patchDataRoom(
      {
        description: trimmedDescription || null,
        id: dataRoomId,
        name: trimmedName,
        showInFolderStats: form.showInFolderStats,
        status: form.isActive ? DataRoomStatus.ACTIVE : DataRoomStatus.INACTIVE,
      },
      { onSuccess: () => handleDismiss() },
    );
  };

  // The room's stored status, not the form's: the subtitle reports what is saved, and the toggle
  // below is what proposes a change to it.
  const isActive = dataRoom?.status === DataRoomStatus.ACTIVE;

  return (
    <ModalWrapper
      className="w-full max-w-[493px] shadow-none"
      control={{ onOpenChange: (open) => !open && handleDismiss(), open: isOpen }}
      onBgClick={handleDismiss}
      onOpenAutoFocus={keepFocusInsideDialog}
      side="CENTER"
    >
      <div className="flex w-full flex-col gap-8 overflow-hidden rounded bg-white p-4">
        {/* Name, a dot, then the status in the colour it carries everywhere else — the room says
            what state it is in before the toggle below asks you to change it. */}
        <div className="flex flex-col border-b border-gray-100 pb-4">
          <h2 className="text-xl font-semibold leading-8 text-gray-700">
            <AppFormattedMessage id="DATA_ROOM_SETTINGS" />
          </h2>
          <div className="flex items-center gap-3">
            <span className="truncate text-label-sm font-medium text-gray-500">
              {dataRoom?.name ?? ''}
            </span>
            <span aria-hidden className="size-1 shrink-0 rounded-full bg-gray-300" />
            {/* The size stays out of twMerge: `text-label-sm` is not one of the sizes it knows,
                so beside a `text-…` colour it is taken for a second colour and dropped, and the
                status falls back to the inherited 16px. */}
            <span
              className={`text-label-sm font-medium ${
                isActive ? 'text-forest-600' : 'text-sunbeam-700'
              }`}
            >
              <AppFormattedMessage id={isActive ? 'ACTIVE' : 'INACTIVE'} />
            </span>
          </div>
        </div>

        {isLoading || !form ? (
          <Loader />
        ) : (
          <div className="flex flex-col gap-4">
            {/* Both fields stop at the limit, so the next keystroke is dropped in silence unless
                the field says why — the same message the create flow shows. */}
            <div className="flex flex-col gap-1">
              <Input
                maxLength={MAX_SPACE_NAME_LENGTH}
                onChange={(name) => setForm((current) => current && { ...current, name })}
                placeholder={<AppFormattedMessage id="DATA_ROOM_NAME" />}
                value={form.name}
              />
              {form.name.length >= MAX_SPACE_NAME_LENGTH && (
                <CharacterLimitMessage count={MAX_SPACE_NAME_LENGTH} />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <div className="relative h-[104px] overflow-hidden rounded-t border-b border-gray-600 bg-gray-50 px-3">
                <textarea
                  className="h-full w-full resize-none bg-transparent pb-6 pt-[18px] font-inter text-sm font-[450] leading-5 text-gray-700 outline-none"
                  maxLength={MAX_SPACE_DESCRIPTION_LENGTH}
                  onChange={(event) =>
                    setForm((current) => current && { ...current, description: event.target.value })
                  }
                  placeholder={messagesLocale['ENTER_DATA_ROOM_DESCRIPTION']}
                  value={form.description}
                />
                <span className="pointer-events-none absolute bottom-2 right-3 font-inter text-label-sm font-medium text-gray-400">
                  {form.description.length}/{MAX_SPACE_DESCRIPTION_LENGTH}
                </span>
              </div>
              {form.description.length >= MAX_SPACE_DESCRIPTION_LENGTH && (
                <CharacterLimitMessage count={MAX_SPACE_DESCRIPTION_LENGTH} />
              )}
            </div>
            <div className="flex flex-col gap-2">
              {/* A tinted panel rather than an outlined one, and the state reads in its own colour
                under the label — green while the room is open, amber once it is not. */}
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                <div className="flex flex-col">
                  <span className="text-sm font-[550] leading-5 text-gray-700">
                    <AppFormattedMessage id="DATA_ROOM_STATUS" />
                  </span>
                  <span
                    className={twMerge(
                      'text-sm font-[450] leading-5',
                      form.isActive ? 'text-forest-600' : 'text-sunbeam-700',
                    )}
                  >
                    <AppFormattedMessage id={form.isActive ? 'ACTIVE' : 'INACTIVE'} />
                  </span>
                </div>
                <Switch
                  checked={form.isActive}
                  onChange={(isActive) => setForm((current) => current && { ...current, isActive })}
                  toggleClassName={KNOB_SHADOW}
                />
              </div>

              {/* 20.2.4 section 3: whether a folder tells its reader how much is inside it. The
                column and the contract were already there; nothing turned it on. */}
              <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
                <div className="flex flex-col">
                  <span className="text-sm font-[550] leading-5 text-gray-700">
                    <AppFormattedMessage id="SHOW_IN_FOLDER_STATS" />
                  </span>
                  {/* Off reads in the same amber as the room's own Inactive above it: two
                      switches in one panel, one way of saying a thing is not on. */}
                  <span
                    className={twMerge(
                      'text-sm font-[450] leading-5',
                      form.showInFolderStats ? 'text-forest-600' : 'text-sunbeam-700',
                    )}
                  >
                    <AppFormattedMessage id={form.showInFolderStats ? 'ACTIVE' : 'INACTIVE'} />
                  </span>
                </div>
                <Switch
                  checked={form.showInFolderStats}
                  onChange={(showInFolderStats) =>
                    setForm((current) => current && { ...current, showInFolderStats })
                  }
                  toggleClassName={KNOB_SHADOW}
                />
              </div>
            </div>

            {/* 20.2.4 section 3 puts the way in here. The confirmation is its own modal and the
                store holds one at a time, so this hands over rather than stacking. */}
            <button
              className="w-fit px-4 py-2 text-sm font-[550] leading-5 text-fireside-600"
              onClick={() =>
                openModal({
                  metadata: { spaceId: dataRoomId },
                  type: DataRoomModalType.DELETE_DATA_ROOM,
                })
              }
              type="button"
            >
              <AppFormattedMessage id="DELETE_DATA_ROOM" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button
            className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            onClick={handleDismiss}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="h-11 w-fit px-6"
            disabled={!trimmedName || !isDirty || isPending || isLoading}
            isLoading={isPending}
            onClick={save}
          >
            <AppFormattedMessage id="SAVE" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
